import api from './api';
import { isValidObjectId } from '../utils/mongoUtils';

export interface Route {
  _id?: string;
  name: string;
  description?: string;
  technician?: any;
  date?: Date | string; // Campo esperado pelo backend
  serviceRequests?: string[]; // Array de IDs de chamados
  status?: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
  optimized?: boolean;
  startTime?: Date | string;
  endTime?: Date | string;
  notes?: string;
  totalDistance?: number;
  estimatedDuration?: number; // Campo esperado pelo backend
  actualDuration?: number;
  completedRequests?: number;
  totalRequests?: number;
  createdAt?: string;
  updatedAt?: string;
  
  // Campos adicionais usados apenas no frontend
  executionDate?: Date | string | undefined;
  startLocation?: {
    lat: number;
    lng: number;
    address?: string;
  };
  waypoints?: any[];
  estimatedTime?: number; // Alias para estimatedDuration
}

export interface RouteParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  technician?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

const calculateOptimizedRoute = async (waypoints: any) => {
  try {
    console.log('Enviando waypoints para otimização:', waypoints);
    const response = await api.post('/routes/optimize', { waypoints });
    console.log('Resposta da API de otimização:', response.data);
    return {
      data: response.data,
      success: true
    };
  } catch (error: any) {
    console.error('Erro ao calcular rota otimizada:', error);
    console.error('Detalhes do erro:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    return { 
      error: error.response?.data?.message || error.message, 
      data: null,
      success: false
    };
  }
};

const createRoute = async (routeData: Route) => {
  try {
    console.log('Iniciando criação de rota com dados:', JSON.stringify(routeData, null, 2));
    
    // Remover campos que não são esperados pelo backend
    const { waypoints, startLocation, executionDate, estimatedTime, ...backendRouteData } = routeData;
    
    // Validar os dados antes de enviar para o backend
    if (!backendRouteData.name) {
      console.error('Nome da rota é obrigatório');
      return { error: 'Nome da rota é obrigatório', data: null };
    }
    
    if (!backendRouteData.date) {
      console.error('Data de execução é obrigatória');
      return { error: 'Data de execução é obrigatória', data: null };
    }
    
    if (!backendRouteData.serviceRequests || !Array.isArray(backendRouteData.serviceRequests) || backendRouteData.serviceRequests.length === 0) {
      console.error('Pelo menos um chamado de serviço deve ser selecionado');
      return { error: 'Pelo menos um chamado de serviço deve ser selecionado', data: null };
    }
    
    // Validar se todos os IDs de chamados são ObjectIds válidos
    const invalidServiceRequests = backendRouteData.serviceRequests.filter(id => !isValidObjectId(id));
    if (invalidServiceRequests.length > 0) {
      console.error('Chamados com IDs inválidos:', invalidServiceRequests);
      return { 
        error: `A rota contém ${invalidServiceRequests.length} chamado(s) com IDs inválidos.`, 
        data: null 
      };
    }
    
    console.log('Dados filtrados para envio ao backend:', JSON.stringify(backendRouteData, null, 2));
    
    const response = await api.post('/routes', backendRouteData);
    console.log('Resposta do backend:', response);
    return response;
  } catch (error: any) {
    console.error('Erro ao criar rota:', error);
    console.error('Detalhes do erro:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    return { error: error.response?.data?.message || error.message, data: null };
  }
};

const getRouteById = async (id: string) => {
  console.log(`Iniciando busca da rota com ID: ${id}`);
  
  if (!id || !isValidObjectId(id)) {
    console.warn(`ID de rota inválido: ${id}. Não é um ObjectId válido do MongoDB.`);
    return { 
      error: `ID de rota inválido: ${id}. Não é um ObjectId válido do MongoDB.`, 
      data: null 
    };
  }
  
  try {
    console.log(`Enviando requisição para /routes/${id}`);
    const response = await api.get(`/routes/${id}`);
    
    if (!response.data) {
      console.error(`Resposta vazia para a rota ${id}`);
      return { 
        error: `Resposta vazia para a rota ${id}`, 
        data: null 
      };
    }
    
    console.log(`Rota ${id} carregada com sucesso:`, response.data);
    
    // Processar os dados da rota para garantir compatibilidade com o frontend
    const processedData = {
      ...response.data,
      // Garantir que executionDate seja definido a partir de date
      executionDate: response.data.date,
      // Mapear estimatedDuration para estimatedTime para compatibilidade com o frontend
      estimatedTime: response.data.estimatedTime || response.data.estimatedDuration
    };
    
    // Verificar se a rota tem waypoints
    if (!processedData.waypoints && processedData.serviceRequests && processedData.serviceRequests.length > 0) {
      console.log(`Rota ${id} não tem waypoints, mas tem ${processedData.serviceRequests.length} chamados associados`);
    }
    
    return { data: processedData };
  } catch (error: any) {
    console.error(`Erro ao buscar rota ${id}:`, error);
    console.error('Detalhes do erro:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    
    return { 
      error: error.response?.data?.message || error.message || `Erro ao buscar rota ${id}`, 
      data: null 
    };
  }
};

const startRoute = async (id: string, data: any) => {
  if (!id || !isValidObjectId(id)) {
    return { 
      error: `ID de rota inválido: ${id}`, 
      data: null 
    };
  }
  
  try {
    const response = await api.put(`/routes/${id}/start`, data);
    return response;
  } catch (error: any) {
    console.error(`Error starting route ${id}:`, error);
    return { error: error.response?.data?.message || error.message, data: null };
  }
};

const completeRoute = async (id: string, data: any) => {
  if (!id || !isValidObjectId(id)) {
    return { 
      error: `ID de rota inválido: ${id}`, 
      data: null 
    };
  }
  
  try {
    const response = await api.put(`/routes/${id}/complete`, data);
    return response;
  } catch (error: any) {
    console.error(`Error completing route ${id}:`, error);
    return { error: error.response?.data?.message || error.message, data: null };
  }
};

const deleteRoute = async (id: string) => {
  if (!id || !isValidObjectId(id)) {
    return { 
      error: `ID de rota inválido: ${id}`, 
      data: null 
    };
  }
  
  try {
    const response = await api.delete(`/routes/${id}`);
    return { 
      error: null, 
      data: response.data,
      status: response.status
    };
  } catch (error: any) {
    console.error(`Error deleting route ${id}:`, error);
    return { 
      error: error.response?.data?.message || error.message, 
      data: null,
      status: error.response?.status || 500
    };
  }
};

const getRoutes = async (params: RouteParams = {}) => {
  try {
    console.log('Buscando rotas com parâmetros:', params);
    
    const response = await api.get('/routes', { params });
    console.log('Resposta da API de rotas:', response.data);
    
    // Verificar o formato da resposta e normalizá-la
    let normalizedResponse: { 
      routes: any[], 
      pagination: { total: number, page: number, limit: number },
      error?: string 
    } = { 
      routes: [], 
      pagination: { total: 0, page: 1, limit: 10 } 
    };
    
    if (response.data) {
      // Caso 1: Resposta já está no formato esperado com routes e pagination
      if (response.data.routes && Array.isArray(response.data.routes)) {
        console.log(`Formato 1: Encontradas ${response.data.routes.length} rotas na resposta`);
        normalizedResponse = response.data;
      }
      // Caso 2: Resposta é um array de rotas
      else if (Array.isArray(response.data)) {
        console.log(`Formato 2: Encontradas ${response.data.length} rotas na resposta (array direto)`);
        normalizedResponse.routes = response.data;
        normalizedResponse.pagination.total = response.data.length;
      }
      // Caso 3: Outro formato inesperado
      else {
        console.warn('Formato de resposta inesperado:', response.data);
      }
    }
    
    // Processar as rotas para garantir compatibilidade
    if (normalizedResponse.routes && normalizedResponse.routes.length > 0) {
      normalizedResponse.routes = normalizedResponse.routes.map((route: any) => {
        console.log('Processando rota:', route);
        
        // Garantir que executionDate seja definido a partir de date
        const processedRoute = {
          ...route,
          executionDate: route.date,
          // Garantir que estimatedTime esteja definido
          estimatedTime: route.estimatedTime || route.estimatedDuration
        };
        
        // Garantir que o campo technician.name esteja definido
        if (processedRoute.technician && processedRoute.technician.displayName && !processedRoute.technician.name) {
          processedRoute.technician.name = processedRoute.technician.displayName;
        }
        
        console.log('Rota processada:', processedRoute);
        return processedRoute;
      });
    } else {
      console.warn('Nenhuma rota encontrada para processar');
    }
    
    return normalizedResponse;
  } catch (error: any) {
    console.error('Erro ao buscar rotas:', error);
    console.error('Detalhes do erro:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    
    return { 
      error: error.response?.data?.message || error.message, 
      routes: [],
      pagination: { total: 0, page: 1, limit: 10 }
    } as { routes: any[], pagination: { total: number, page: number, limit: number }, error: string };
  }
};

const routeService = {
  calculateOptimizedRoute,
  createRoute,
  getRouteById,
  getRoutes,
  startRoute,
  completeRoute,
  deleteRoute
};

export default routeService;